import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { DviInspection, DviItem, DviDecision } from '../../api/types';
import { publicFetchDvi, publicDviDecision } from '../../api/dvi';
import { t } from '../../lib/i18n';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 });
const DOT: Record<string, string> = { green: 'bg-green-500', yellow: 'bg-yellow-500', red: 'bg-red-500', na: 'bg-gray-300' };

export default function DviReport() {
  const { token } = useParams();
  const [rep, setRep] = useState<DviInspection | null>(null);
  const [err, setErr] = useState(false);
  const c = t.dvi;

  useEffect(() => {
    let a = true;
    publicFetchDvi(String(token)).then((r) => a && setRep(r)).catch(() => a && setErr(true));
    return () => { a = false; };
  }, [token]);

  const decide = (it: DviItem, decision: DviDecision) => {
    setRep((prev) => prev ? { ...prev, items: (prev.items ?? []).map((x) => x.id === it.id ? { ...x, customer_decision: decision } : x) } : prev);
    publicDviDecision(String(token), it.id, decision).catch(() => { /* optimistic; ignore */ });
  };

  const byCat = useMemo(() => {
    const m = new Map<string, DviItem[]>();
    for (const it of rep?.items ?? []) { const a = m.get(it.category) ?? []; a.push(it); m.set(it.category, a); }
    return [...m.entries()];
  }, [rep]);

  if (err) return <div className="min-h-screen grid place-items-center p-6 text-tremor-content">{c.report.empty}</div>;
  if (!rep) return <div className="min-h-screen grid place-items-center p-6">…</div>;

  return (
    <div className="min-h-screen bg-tremor-background text-tremor-content-strong">
      <div className="max-w-xl mx-auto p-4 space-y-4">
        <header className="border-b border-tremor-border pb-3">
          <h1 className="text-lg font-semibold">{c.report.title}</h1>
          <p className="text-sm text-tremor-content">{rep.vehicle_desc} · {rep.vehicle_plate}</p>
          <p className="text-xs text-tremor-content">{rep.customer_name} · {c.report.tech}: {rep.tech_name} · {rep.completed_at?.slice(0, 10)}</p>
          <div className="flex gap-3 mt-2 text-sm">
            <span>🟢 {rep.summary.green}</span><span>🟡 {rep.summary.yellow}</span><span>🔴 {rep.summary.red}</span>
          </div>
        </header>

        {byCat.map(([cat, items]) => (
          <section key={cat}>
            <h2 className="text-sm font-semibold mb-2">{cat}</h2>
            <div className="space-y-2">
              {items.map((it) => (
                <div key={it.id} className="flex gap-3 items-start border-b border-tremor-border pb-2">
                  <span className={`mt-1 w-3 h-3 rounded-full shrink-0 ${DOT[it.status]}`} />
                  <div className="flex-1">
                    <div className="text-sm">{it.name}</div>
                    {it.note && <div className="text-xs text-tremor-content">{it.note}</div>}
                    {it.photo && <img src={it.photo} alt="" className="mt-1 max-h-40 rounded" />}
                    {it.recommended && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-sm font-medium tabular-nums">{fmtMxn.format(it.est_price)}</span>
                        {it.customer_decision === 'pending' ? (
                          <>
                            <button className="px-3 py-1 rounded bg-green-600 text-white text-xs" onClick={() => decide(it, 'approved')}>{c.decision.approve}</button>
                            <button className="px-3 py-1 rounded bg-tremor-background-muted text-xs" onClick={() => decide(it, 'declined')}>{c.decision.decline}</button>
                          </>
                        ) : (
                          <span className={`text-xs ${it.customer_decision === 'approved' ? 'text-green-600' : 'text-tremor-content'}`}>
                            {it.customer_decision === 'approved' ? c.decision.approved : c.decision.declined}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}

        <footer className="border-t border-tremor-border pt-3 flex justify-between text-sm font-semibold">
          <span>{c.recommendedTotal}</span>
          <span className="tabular-nums">{fmtMxn.format(rep.recommended_total)}</span>
        </footer>
      </div>
    </div>
  );
}
